import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { PREPOPULATE_INPUT } from "@/utils/prepopulate_input.js";
import { API_BASE_URL } from "@/config";
import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";

import { pdf_decryptify } from "@/utils/pdf_management.js";

var absa = await import("@/assets/images/banks/absa.svg");
var standardbank = await import("@/assets/images/banks/standardbank.svg");
var fnb = await import("@/assets/images/banks/fnb.svg");
var nedbank = await import("@/assets/images/banks/nedbank.svg");
var capitec = await import("@/assets/images/banks/capitec.svg");
var discovery = await import("@/assets/images/banks/discovery.svg");
var tymebank = await import("@/assets/images/banks/tymebank.svg");
var easypay = await import("@/assets/images/banks/easypay.svg");
var investec = await import("@/assets/images/banks/investec.svg");
var rmb = await import("@/assets/images/banks/rmb.svg");

export default {
  name: "AllInOneBankStatementVerification",
  mixins: [MAIN_CONTROLLER, PREPOPULATE_INPUT],
  components: {
    BreadCrumb,
    ClientLogo,
  },
  data() {
    return {
      screen_width: window.innerWidth,
      // Step management
      current_step: 1,
      is_loading: false,
      first_name: "",
      last_name: "",
      id_number: "",
      email: "",
      is_field_disabled: {
        first_name: false,
        last_name: false,
        id_number: false,
        email: false,
      },
      selected_bank: "",
      other_bank_name: "",

      // Verification method
      verification_method: "",

      // Online banking integration
      bank_account_id: null,
      customer_id: null,
      external_link: "", // Bank lists
      list_a_banks: [
        {
          value: "standard-bank",
          title: "Standard Bank",
          svg_path: standardbank.default,
        },
        { value: "absa", title: "ABSA Bank", svg_path: absa.default },
        {
          value: "fnb",
          title: "First National Bank (FNB)",
          svg_path: fnb.default,
        },
        { value: "nedbank", title: "Nedbank", svg_path: nedbank.default },
        { value: "capitec", title: "Capitec Bank", svg_path: capitec.default },
        {
          value: "discovery",
          title: "Discovery Bank",
          svg_path: discovery.default,
        },
      ],
      list_b_banks: [
        { value: "easypay", title: "Easypay", svg_path: easypay.default },
        { value: "investec", title: "Investec", svg_path: investec.default },
        { value: "rmb", title: "RMB", svg_path: rmb.default },
        { value: "tyme-bank", title: "Tyme Bank", svg_path: tymebank.default },
      ], // File uploads
      selected_files: [],

      // Validation and processing
      validation_errors: {},
      is_processing: false,
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    all_fields_filled() {
      return (
        this.first_name.trim() &&
        this.last_name.trim() &&
        this.id_number.trim() &&
        this.email.trim()
      );
    },
    is_list_a_bank() {
      return this.list_a_banks.some(
        (bank) => bank.value === this.selected_bank
      );
    },
    is_list_b_bank() {
      return this.list_b_banks.some(
        (bank) => bank.value === this.selected_bank
      );
    },
    is_other_bank() {
      return this.selected_bank && !this.is_list_a_bank && !this.is_list_b_bank;
    },
    can_proceed_to_step_2() {
      return (
        this.first_name.trim() &&
        this.last_name.trim() &&
        this.id_number.trim() &&
        this.email.trim() &&
        this.validate_email(this.email)
      );
    },
    can_proceed_to_bank_selection() {
      return (
        this.selected_bank &&
        (this.selected_bank !== "other" || this.other_bank_name.trim() !== "")
      );
    },
    can_proceed_to_step_3() {
      return this.can_proceed_to_bank_selection;
    },
    can_proceed_to_step_4() {
      if (this.is_list_a_bank) {
        return this.verification_method !== "";
      } else if (this.is_list_b_bank || this.selected_bank === "other") {
        return this.other_bank_name.trim() !== ""; // Ensure other bank name is provided
      } else {
        return false;
      }
    },
    verification_method_display() {
      if (this.verification_method === "online") {
        return "Online Banking";
      } else if (this.verification_method === "manual") {
        return "Manual Upload";
      } else if (this.is_other_bank) {
        return "Custom Form Upload";
      }
      return "";
    },
    can_proceed() {
      // First check basic personal info
      if (
        !this.selected_bank ||
        !this.first_name ||
        !this.last_name ||
        !this.id_number ||
        !this.email ||
        !this.validate_email(this.email)
      ) {
        return false;
      }

      // Check bank specific requirements
      if (this.selected_bank === "other" && !this.other_bank_name) {
        return false;
      } // Check verification method requirements
      if (this.is_list_a_bank) {
        if (this.verification_method === "online") {
          return true; // Online banking connection will be handled in step 3
        } else if (this.verification_method === "manual") {
          return this.selected_files.length > 0;
        } else {
          return false; // No verification method selected
        }
      } else if (this.is_list_b_bank || this.is_other_bank) {
        return this.selected_files.length > 0;
      }

      return false;
    },
    selected_bank_display() {
      if (this.selected_bank === "other") {
        return this.other_bank_name;
      }

      const allBanks = [...this.list_a_banks, ...this.list_b_banks];
      const bank = allBanks.find((b) => b.value === this.selected_bank);
      return bank ? bank.title : this.selected_bank;
    },
  },
  methods: {
    pdf_decryptify,
    // Step navigation
    proceed_to_step_2() {
      if (
        this.first_name.trim() &&
        this.last_name.trim() &&
        this.id_number.trim() &&
        this.email.trim()
      ) {
        this.current_step = 2;
      }
    },
    proceed_to_step_3() {
      // Validate bank selection before proceeding
      if (this.selected_bank) {
        // For "other" bank, make sure the bank name is provided
        if (this.selected_bank === "other") {
          if (!this.other_bank_name.trim()) {
            this.validation_errors.other_bank_name =
              "Bank name is required when 'Other Bank' is selected";
            return;
          }
        }

        // Clear any existing validation errors
        if (this.validation_errors.selected_bank) {
          delete this.validation_errors.selected_bank;
        }

        if (this.validation_errors.other_bank_name) {
          delete this.validation_errors.other_bank_name;
        }
        // For List B banks and Other bank, go straight to step 4 with manual verification
        if (this.is_list_b_bank || this.selected_bank === "other") {
          this.verification_method = "manual";
          this.current_step = 4;
        } else {
          // For List A banks, proceed to step 3 for verification method selection
          this.current_step = 3;
        }
      } else {
        // Show validation error
        this.validation_errors.selected_bank = "Please select your bank";
      }
    },
    proceed_to_step_4() {
      // Clear any validation errors
      if (this.validation_errors.verification_method) {
        delete this.validation_errors.verification_method;
      }
      // Check if we can proceed based on the bank type
      if (this.is_list_a_bank) {
        // For List A banks, we need to select a verification method
        if (!this.verification_method) {
          this.validation_errors.verification_method =
            "Please select a verification method";
          return;
        }
      } else if (this.is_list_b_bank || this.selected_bank === "other") {
        // For List B banks and Other banks, manual verification is auto-selected
        this.verification_method = "manual";
      }

      // Proceed to the final step
      this.current_step = 4;

      // Automatically initiate online banking if that's the selected method
      if (this.verification_method === "online" && !this.bank_account_id) {
        this.$nextTick(() => {
          this.initiate_online_banking();
        });
      }
    },

    back_to_step_1() {
      this.current_step = 1;
    },

    back_to_step_2() {
      this.current_step = 2;
    },

    back_to_step_3() {
      this.current_step = 3;
    },

    back_from_process() {
      // For List B banks and Other banks, go back to bank selection (step 2)
      // For List A banks, go back to verification method selection (step 3)
      if (this.is_list_b_bank || this.is_other_bank) {
        this.current_step = 2;
      } else {
        this.current_step = 3;
      }
    },

    select_bank(bankValue) {
      this.selected_bank = bankValue;
      this.verification_method = ""; // Reset verification method on bank change
      this.validation_errors = {}; // Auto-select manual for List B banks and Other bank
      if (this.is_list_b_bank || bankValue === "other") {
        this.verification_method = "manual";
      } // Reset file uploads for other banks
      if (!this.is_other_bank) {
        this.selected_files = [];
      }
    },

    handle_bank_selection() {
      // Clear any existing validation errors
      if (this.validation_errors.selected_bank) {
        delete this.validation_errors.selected_bank;
      }

      if (this.validation_errors.other_bank_name) {
        delete this.validation_errors.other_bank_name;
      }

      // Reset verification method when bank changes
      this.verification_method = ""; // Auto-select manual verification for List B banks and Other bank
      if (this.is_list_b_bank || this.selected_bank === "other") {
        this.verification_method = "manual";
      } // Reset bank account id and external link when bank changes
      this.bank_account_id = null;
      this.external_link = "";

      // Reset file uploads when switching between banks
      this.selected_files = [];
    },
    select_verification_method(method) {
      this.verification_method = method;
    },

    trigger_file_upload() {
      this.$refs.file_input.click();
    },

    validate_max_files(files) {
      return files.length <= 6;
    },

    validate_file_type(file) {
      const allowed_types = ["application/pdf"];
      if (!allowed_types.includes(file.type)) {
        Swal.fire({
          title: "Invalid File Type",
          text: "Only PDF files are allowed",
          icon: "warning",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
        return false;
      }
      return true;
    },

    async handle_file_upload(event) {
      const files_returned = Array.from(event.target.files)
      if (!files_returned.length) {
        event.target.value = "";
        return;
      }
      
      const processed_files = [];
      for (const file of files_returned) {
          try {
              const result = await this.pdf_decryptify(file);
              
              // If user cancels, result may be a string or null, skip this file
              if (!result || typeof result === "string" || !result.type) {
                continue;
              }
              processed_files.push(result);
          } catch (err) {
              console.error('Error processing file:', err);
              // Skip this file and continue
              continue;
          }
      }
      event.target.value = "";
      const new_files = processed_files;
      if (new_files.length === 0) {
        return;
      }

      const files = new_files.filter((file) =>
        this.validate_file_type(file)
      );

      if (files && files.length > 0) {
        // Limit to max 6 files
        const total_files = this.selected_files.length + files.length;
        if (total_files > 6) {
          Swal.fire({
            title: "Maximum Files Reached",
            text: "You can upload a maximum of 6 files",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });

          // Only add files up to the limit
          const remaining_slots = 6 - this.selected_files.length;
          if (remaining_slots > 0) {
            this.selected_files = [
              ...this.selected_files,
              ...files.slice(0, remaining_slots),
            ];
          }
        } else {
          this.selected_files = [...this.selected_files, ...files];
        }
      }
    },

    async handle_file_drop(event) {
      event.preventDefault();
      
      const files_returned = Array.from(event.dataTransfer.files)
      const processed_files = [];
      for (const file of files_returned) {
          try {
              const result = await this.pdf_decryptify(file);
              // If user cancels, result may be a string or null, skip this file
              if (!result || typeof result === "string" || !result.type) {
                continue;
              }
              processed_files.push(result);
          } catch (err) {
              console.error('Error processing file:', err);
              continue;
          }
      }
      const new_files = processed_files;
      if (new_files.length === 0) {
        return;
      }

      const files = new_files.filter((file) =>
        this.validate_file_type(file)
      );
      if (files && files.length > 0) {
        // Limit to max 6 files
        const total_files = this.selected_files.length + files.length;
        if (total_files > 6) {
          Swal.fire({
            title: "Maximum Files Reached",
            text: "You can upload a maximum of 6 files",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });

          // Only add files up to the limit
          const remaining_slots = 6 - this.selected_files.length;
          if (remaining_slots > 0) {
            this.selected_files = [
              ...this.selected_files,
              ...files.slice(0, remaining_slots),
            ];
          }
        } else {
          this.selected_files = [...this.selected_files, ...files];
        }
      }
    },

    remove_file(index) {
      this.selected_files.splice(index, 1);
    },
    validate_form() {
      this.validation_errors = {};

      if (!this.first_name.trim()) {
        this.validation_errors.first_name = "First name is required";
      }

      if (!this.last_name.trim()) {
        this.validation_errors.last_name = "Last name is required";
      }

      if (!this.id_number.trim()) {
        this.validation_errors.id_number = "ID number is required";
      }

      if (!this.email.trim()) {
        this.validation_errors.email = "Email address is required";
      } else if (!this.validate_email(this.email)) {
        this.validation_errors.email = "Please enter a valid email address";
      }

      // Improved bank selection validation
      if (!this.selected_bank) {
        this.validation_errors.selected_bank = "Please select your bank";
      } else if (
        this.selected_bank === "other" &&
        !this.other_bank_name.trim()
      ) {
        this.validation_errors.other_bank_name =
          "Bank name is required when 'Other Bank' is selected";
      }

      // Validation for verification method
      if (
        this.current_step >= 3 &&
        this.is_list_a_bank &&
        !this.verification_method
      ) {
        this.validation_errors.verification_method =
          "Please select a verification method";
      } // Validation for bank statements if manual verification is selected
      if (
        this.current_step >= 4 &&
        (this.verification_method === "manual" ||
          this.is_other_bank ||
          this.is_list_b_bank)
      ) {
        if (this.selected_files.length === 0) {
          this.validation_errors.bank_statements =
            "Please upload at least one bank statement file";
        }
      }

      return Object.keys(this.validation_errors).length === 0;
    },

    validate_email(email) {
      const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email_regex.test(email);
    },

    initiate_online_banking() {
      this.is_processing = true;

      let bank_type;

      if (this.selected_bank) {
        if (this.is_list_a_bank) {
          bank_type = this.verification_method;
        } else if (this.is_list_b_bank) {
          bank_type = "manual";
        } else {
          return;
        }
      } else {
        // Show error alert for bank selection required
        Swal.fire({
          title: "Bank Selection Required",
          text: "Please select a bank before continuing.",
          icon: "warning",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
        this.is_processing = false;
        return;
      }

      const token = localStorage.getItem("token");

      // Make API request to initiate online banking
      fetch(
        `${API_BASE_URL}/verify_api/compliance_verification/many_solution_online_login/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({
            bank: this.selected_bank,
            first_name: this.first_name,
            last_name: this.last_name,
            id_number: this.id_number,
            email: this.email,
            bank_type: bank_type,
            workflow_id: this.workflow_id,
          }),
        }
      )
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          this.bank_account_id = data.bank_account_id;
          this.customer_id = data.customer_id;
          this.external_link = data.portal_url;
          this.is_processing = false;

          Swal.close();
        })
        .catch((error) => {
          console.error("Failed to get online login url", error);
          // Show error alert
          Swal.fire({
            title: "Initiation Failed",
            text: "Failed to retrieve bank account id. Please try again later.",
            icon: "error",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });

          this.is_processing = false;
        });
    },

    proceed_verification() {
      // Set processing state
      this.is_processing = true;

      // Create form data to handle file uploads
      const form_data = new FormData();

      // Add personal info
      form_data.append("id_number", this.id_number);
      form_data.append("email", this.email);
      form_data.append("first_name", this.first_name);
      form_data.append("last_name", this.last_name);
      form_data.append("bank", this.selected_bank);
      form_data.append("workflow_id", this.workflow_id);

      // Determine bank_type based on bank selection
      let bank_type;
      if (this.is_list_a_bank || this.is_list_b_bank) {
        bank_type = "manual";
      } else {
        bank_type = "custom";
      }

      form_data.append("bank_type", bank_type);
      // Check if files are uploaded for manual verification
      if (
        this.verification_method === "manual" ||
        this.is_other_bank ||
        this.is_list_b_bank
      ) {
        if (this.selected_files.length === 0) {
          Swal.fire({
            title: "Missing Files",
            text: "Please upload at least one bank statement file before proceeding.",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          this.is_processing = false;
          return;
        }

        // Append each file to the form data as an array
        this.selected_files.forEach((file) => {
          form_data.append("files", file);
        });
      }

      // Get token for authorization
      const token = localStorage.getItem("token");

      // Show loading indicator
      Swal.fire({
        title: "Processing",
        html: "Please wait while we process your verification...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Make API request
      fetch(
        `${API_BASE_URL}/verify_api/compliance_verification/many_solution_manual_upload/`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${token}`,
          },
          body: form_data,
        }
      )
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          this.is_processing = false;
          
          // Check if response contains error
          if (data.error) {
          try {
            const errors = JSON.parse(data.error);
            const layoutError = errors.find(err => err.code === 'invalid_layout');
            const bankError = errors.find(err => err.code === 'invalid_bank');
            
            if (layoutError) {
            Swal.fire({
              title: "Invalid Document",
              text: "The uploaded document format could not be recognized. Please ensure you're uploading a valid bank statement from your selected bank.",
              icon: "error",
              confirmButtonColor: "#3085d6",
              confirmButtonText: "OK",
            });
            return;
            }
            
            if (bankError) {
            Swal.fire({
              title: "Bank Mismatch",
              text: bankError.data?.error || "The uploaded statement does not match your selected bank. Please check if you've selected the correct bank or upload the correct statement.",
              icon: "error",
              confirmButtonColor: "#3085d6",
              confirmButtonText: "OK",
            });
            return;
            }
          } catch (parseError) {
            // If parsing fails, show generic error
            console.error('Error parsing error response:', parseError);
          }
          
          // Show generic error for other types
          Swal.fire({
            title: "Validation Error",
            text: "There was an error validating your bank statement. Please check your files and try again.",
            icon: "error",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
          return;
          }

          // Success case
          Swal.fire({
            title: "Verification Submitted",
            text: "Your bank statement verification has been submitted successfully.",
            icon: "success",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });

          this.fetch_current_service();
        })
        .catch((error) => {
          this.is_processing = false;
          console.error('Request failed:', error);

          Swal.fire({
            title: "Submission Failed",
            text: "There was an error submitting your verification. Please try again.",
            icon: "error",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
        });
    },
    async get_customer_data() {
      this.is_loading = true;

      const token = localStorage.getItem("token");
      const customer_data_url = `${API_BASE_URL}/verify_api/system_management_api/get_customer_data/`;

      try {
        const response = await fetch(customer_data_url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();

        // Populate fields and set disabled state based on whether data exists
        this.first_name = data.first_name || "";
        this.last_name = data.last_name || "";
        this.id_number = data.id_number || "";
        this.email = data.email || "";

        // Only disable fields that have prepopulated data
        this.is_field_disabled = {
          first_name: !!data.first_name,
          last_name: !!data.last_name,
          id_number: !!data.id_number,
          email: !!data.email,
        };

        // Auto-continue if all fields are already filled and valid
        if (this.all_fields_filled && this.validate_form()) {
          this.proceed_to_step_2();
        }

        this.is_loading = false;
      } catch (error) {
        this.is_loading = false;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load customer data",
          timer: 3000,
          timerProgressBar: true,
        });
      }
    },
  },

  created() {
    this.get_customer_data();
  },
};
